import { InputAdornment, makeStyles, TextField, Theme } from '@freighthub/silhouette-core'
import { Container, UI, useShipmentDetails } from 'features/ShipmentDetails'
import { containerTypeGroupTerms } from 'features/TransportUnits'
import { ChangeEvent, FC, MouseEvent, useState } from 'react'
import { useI18n } from 'utils/localisation'

import { IContainerTP } from '../service/TPContainersPlans'
import { isValidContainerNumber } from '../service/validate'
import { ArrowDownCollapser } from './ArrowDownCollapser'
import { Cell, Row } from './TPPartials'

const testIds = {
  containerNumberInput: 'container-number-input',
}

type TPContainerRowProps = {
  container: Container
  containerTp: IContainerTP
  ui: UI
  i: number
  active?: boolean
  onClick?: () => void
}

const useStyles = makeStyles<Theme>((theme) => ({
  paddedCell: {
    padding: theme.spacing(1, 2, 1, 0),
  },
}))

export const TPContainerRow: FC<TPContainerRowProps> = ({ container, containerTp, i, ui, active, onClick }) => {
  const classes = useStyles()
  const localisationStore = useI18n()
  const [blurred, setBlurred] = useState(false)
  const [containerState, setContainerState] = useState(() => {
    const correlationContainerNumber = containerTp?.main?.trackables?.[0].correlation?.containerNumber || ''

    return {
      ...container,
      containerNumber: container?.containerNumber || correlationContainerNumber,
    }
  })
  const { shipment, updateShipment } = useShipmentDetails()

  const { translate } = localisationStore

  const hasContainer = containerState?.containerNumber && containerState.containerNumber.length > 0

  // Once the container number is provided, and it cannot be empty
  // the validation is checked
  // if it is invalid, the error text appears
  const containerNumberError =
    blurred && hasContainer && !isValidContainerNumber(containerState.containerNumber)
      ? translate('shipment_details-containers-container_number_incorrect')
      : ''

  const onContainerInput = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value, name } = e.target
    if (name === 'containerNumber') {
      // Only if the container number is valid is the actual container number value updated
      // other wise it is rejected
      if (isValidContainerNumber(value) || value.length === 0) {
        container.containerNumber = value
      }
    } else {
      container[name] = name === 'grossWeight' ? parseFloat(value) : value
    }

    setContainerState((prevState) => ({
      ...prevState,
      [name]: name === 'grossWeight' ? parseFloat(value) : value,
    }))

    const containers = shipment?.containers.map((mappedContainer) => {
      if (mappedContainer._id === container._id) {
        return container
      }

      return mappedContainer
    })

    updateShipment({
      ...shipment,
      containers,
    })
  }

  const handleBlur = () => setBlurred(true)
  const stopPropagation = (e: MouseEvent<HTMLDivElement>) => e.stopPropagation()

  if (!container) {
    return (
      <Row>
        <Cell>#{i + 1}</Cell>
      </Row>
    )
  }

  return (
    <Row>
      <Cell>#{i + 1}</Cell>
      <Cell className={classes.paddedCell} onClick={stopPropagation}>
        <TextField
          disabled={ui.saving} // Disables Editing when the Content is being saved
          name="containerNumber"
          value={containerState?.containerNumber || ''}
          onChange={onContainerInput}
          onBlur={handleBlur}
          helperText={containerNumberError}
          error={Boolean(containerNumberError)}
          inputProps={{
            'data-tracking-id': 'shipment-overview-c_number_input',
            'data-testid': testIds.containerNumberInput,
          }}
          fullWidth
        />
      </Cell>
      <Cell>{localisationStore.translate(containerTypeGroupTerms[container.type])}</Cell>
      <Cell className={classes.paddedCell} onClick={stopPropagation}>
        <TextField
          disabled={ui.saving} // Disables Editing when the Content is being saved
          name="sealNumber"
          value={containerState?.sealNumber || ''}
          onChange={onContainerInput}
          inputProps={{
            'data-tracking-id': 'shipment-overview-c_seal_input',
          }}
          fullWidth
        />
      </Cell>
      <Cell className={classes.paddedCell}>
        <TextField
          disabled={ui.saving} // Disables Editing when the Content is being saved
          value={containerState?.grossWeight || ''}
          type="number"
          inputProps={{
            min: 0,
            step: 0.1,
            'data-tracking-id': 'shipment-overview-c_gross_weight_input',
          }}
          InputProps={{
            endAdornment: <InputAdornment position="end">kg</InputAdornment>,
          }}
          name="grossWeight"
          onChange={onContainerInput}
          fullWidth
        />
      </Cell>
      <Cell className={classes.paddedCell}>
        <TextField
          disabled={ui.saving} // Disables Editing when the Content is being saved
          name="customerReference"
          value={containerState?.customerReference || ''}
          onChange={onContainerInput}
          inputProps={{
            'data-tracking-id': 'shipment-overview-c_customer_reference_input',
          }}
          fullWidth
        />
      </Cell>
      <Cell onClick={onClick}>
        {/* @ts-expect-error TS(2322) FIXME: Type 'boolean | undefined' is not assignable to ty... Remove this comment to see the full error message */}
        <ArrowDownCollapser active={active} />
      </Cell>
    </Row>
  )
}
